import { useState, useEffect, useMemo, useCallback } from 'react';
import { usePriceMetadata } from '@/hooks/usePriceMetadata';
import priceService from '@/services/priceService';
import { formatNumberForDisplay, NumberFormat } from '@/utils/numberFormat';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import { logError, logWarn } from '@/utils/errorHandler';

// Define interfaces for metadata objects
export interface PriceType {
  id: string;
  name: string;
}

export interface Currency {
  id: string;
  code: string;
  name: string;
  symbol: string;
}

export interface Price {
  id: number;
  priceType: string;
  currencyCode: string;
  value: number;
  formattedValue: string;
  validFrom: string | null;
  validTo: string | null;
  isExpired?: boolean;
}

interface PriceSummary {
  min: number | null;
  max: number | null;
  avg: number | null;
  total: number;
}

interface PriceFilters {
  search: string;
  currency: string;
  validTo?: string | null;
}

export function usePricingData(productId: number) {
  // Get organization settings for number formatting
  const { orgSettings } = useOrgSettings();
  const numberFormat = (orgSettings?.number_format || 'dot') as NumberFormat;

  // Use the metadata hook
  const metadataHook = usePriceMetadata();

  // Adapt metadata to our expected shape
  const priceTypes: PriceType[] = useMemo(() => {
    return (metadataHook.priceTypes || []).map((pt: any) => ({
      id: pt.id || pt.value || '',
      name: pt.name || pt.label || '',
    }));
  }, [metadataHook.priceTypes]);

  const currencies: Currency[] = useMemo(() => {
    return (metadataHook.currencies || [])
      .map((cur: any) => ({
        id: cur.id || cur.iso_code || cur.code || '',
        code: cur.iso_code || cur.code || '',
        name: cur.name || cur.label || '',
        symbol: cur.symbol || '',
      }))
      .filter(cur => cur.code && cur.code.trim() !== ''); // Filter out empty codes
  }, [metadataHook.currencies]);

  const [prices, setPrices] = useState<Price[]>([]);
  const [rawPrices, setRawPrices] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<PriceFilters>({
    search: '',
    currency: 'all',
  });

  // Function to fetch prices
  const fetchPrices = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Use priceService to fetch prices
      const response = await priceService.getPrices(productId);

      // Handle different response formats
      let priceData: any[] = [];

      if (response && response.data) {
        if (Array.isArray(response.data)) {
          // Direct array in response.data
          priceData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          // Nested data property (response.data.data)
          priceData = response.data.data;
        } else if (Array.isArray(response.data.results)) {
          // DRF paginated response
          priceData = response.data.results;
        }
      }

      // Default to empty array if no data found in expected formats
      if (!priceData || !Array.isArray(priceData)) {
        logWarn('No price data found or unexpected format', {
          component: 'usePricingData',
          action: 'fetchPrices',
          metadata: { responseData: response.data },
        });
        priceData = [];
      }

      setRawPrices(priceData);

      // Transform the API response to match our Price interface
      const fetchedPrices: Price[] = priceData.map(price => {
        const validTo = price.valid_to || price.validTo || null;
        const now = new Date();
        const isExpired = validTo ? new Date(validTo) < now : false;

        return {
          id: price.id,
          priceType: price.priceType || price.price_type || '',
          currencyCode: price.currencyCode || price.currency || 'USD',
          value: typeof price.value === 'number' ? price.value : Number(price.amount) || 0,
          formattedValue: (() => {
            const numericValue =
              typeof price.value === 'number' ? price.value : Number(price.amount) || 0;
            const currencyCode = price.currencyCode || price.currency || 'USD';
            const formattedNumber = formatNumberForDisplay(numericValue, numberFormat, 2);

            if (numberFormat === 'comma') {
              return `${formattedNumber} ${currencyCode}`;
            } else {
              return `${currencyCode} ${formattedNumber}`;
            }
          })(),
          validFrom: price.valid_from || price.validFrom || null,
          validTo: validTo,
          isExpired: isExpired,
        };
      });

      setPrices(fetchedPrices);
    } catch (err) {
      setError('Failed to load prices');
      logError(
        'Error fetching prices',
        {
          component: 'usePricingData',
          action: 'fetchPrices',
          metadata: { productId, error: err },
        },
        { showToast: false, userFacing: false }
      );
      setPrices([]); // Ensure prices is always an array even on error
      setRawPrices([]);
    } finally {
      setIsLoading(false);
    }
  }, [productId, numberFormat]);

  // Add a new price
  const add = async (payload: any) => {
    try {
      // Transform frontend camelCase fields to backend snake_case fields
      const transformedPayload = {
        price_type: payload.priceType,
        currency: payload.currencyCode,
        amount: payload.value,
        valid_from: payload.validFrom,
        valid_to: payload.validTo || null,
      };

      await priceService.createPrice(productId, transformedPayload);
      await fetchPrices(); // Refresh the list
    } catch (err) {
      logError(
        'Error adding price',
        {
          component: 'usePricingData',
          action: 'add',
          metadata: { productId, payload, error: err },
        },
        { showToast: false, userFacing: false }
      );
      throw err;
    }
  };

  // Function to update a price
  const update = async (priceId: number, payload: any) => {
    try {
      // Transform frontend camelCase fields to backend snake_case fields
      const transformedPayload = {
        price_type: payload.priceType,
        currency: payload.currencyCode,
        amount: payload.value,
        valid_from: payload.validFrom,
        valid_to: payload.validTo || null,
      };

      await priceService.updatePrice(productId, priceId, transformedPayload);
      await fetchPrices(); // Refresh prices after updating
      return true;
    } catch (err) {
      logError(
        'Error updating price',
        {
          component: 'usePricingData',
          action: 'update',
          metadata: { productId, priceId, payload, error: err },
        },
        { showToast: false, userFacing: false }
      );
      throw err;
    }
  };

  // Function to remove a price
  const remove = async (priceId: number) => {
    try {
      await priceService.deletePrice(productId, priceId);
      await fetchPrices(); // Refresh prices after deleting
      return true;
    } catch (err) {
      logError(
        'Error removing price',
        {
          component: 'usePricingData',
          action: 'remove',
          metadata: { productId, priceId, error: err },
        },
        { showToast: false, userFacing: false }
      );
      throw err;
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchPrices();
  }, [fetchPrices]);

  // Filter prices based on current filters
  const filteredPrices = useMemo(() => {
    return prices.filter(price => {
      // Search filter - search in price type code, price type label, and formatted value
      const matchesSearch =
        !filters.search ||
        (() => {
          const searchTerm = filters.search.toLowerCase();
          const priceTypeLabel =
            priceTypes.find(pt => pt.id === price.priceType)?.name || price.priceType;

          return (
            price.priceType.toLowerCase().includes(searchTerm) ||
            priceTypeLabel.toLowerCase().includes(searchTerm) ||
            price.formattedValue.toLowerCase().includes(searchTerm) ||
            price.currencyCode.toLowerCase().includes(searchTerm)
          );
        })();

      // Currency filter
      const matchesCurrency = filters.currency === 'all' || price.currencyCode === filters.currency;

      // Valid to date filter
      const matchesValidTo =
        !filters.validTo ||
        (() => {
          if (!filters.validTo) return true;
          if (!price.validTo) return false;

          const filterDate = new Date(filters.validTo);
          const priceValidTo = new Date(price.validTo);
          return priceValidTo <= filterDate;
        })();

      return matchesSearch && matchesCurrency && matchesValidTo;
    });
  }, [prices, filters, priceTypes]);

  // Calculate summary statistics
  const summary = useMemo<PriceSummary>(() => {
    if (filteredPrices.length === 0) {
      return { min: null, max: null, avg: null, total: 0 };
    }

    const values = filteredPrices.map(p => p.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const sum = values.reduce((a, b) => a + b, 0);
    const avg = sum / values.length;

    return {
      min,
      max,
      avg,
      total: filteredPrices.length,
    };
  }, [filteredPrices]);

  // Get price type label
  const getPriceTypeLabel = (value: string): string => {
    const priceType = priceTypes.find(pt => pt.id === value);
    return priceType ? priceType.name : value;
  };

  // Format price for display
  const formatPrice = (value: number, currencyCode: string): string => {
    const formattedNumber = formatNumberForDisplay(value, numberFormat, 2);

    if (numberFormat === 'comma') {
      return `${formattedNumber} ${currencyCode}`;
    } else {
      return `${currencyCode} ${formattedNumber}`;
    }
  };

  return {
    prices: filteredPrices,
    rawPrices, // full, unfiltered from API
    isLoading,
    error,
    summary,
    filters,
    setFilters,
    refresh: fetchPrices,
    getPriceTypeLabel,
    formatPrice,
    priceTypes,
    currencies,
    add,
    update,
    remove,
  };
}
